<script lang="ts">
import '../Text/text.css';

import { computed } from 'vue';

import type { PhxsgHeadingProps } from '@sg-core/src/View/Components/Atoms/Heading';
import { PhxsgHeading as PhxsgCoreHeading } from '@sg-core/src/View/Components/Atoms/Heading';

export type PiaHeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type PiaHeadingColor = 'primary' | 'secondary' | 'subtle' | 'muted';

export interface PiaPhxsgHeadingProps extends Omit<PhxsgHeadingProps, 'variant' | 'size'> {
  variant?: PiaHeadingVariant;
  color?: PiaHeadingColor;
}
</script>

<script setup lang="ts">
const headingVariants: PiaHeadingVariant[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const { use = 'h2', variant, color = 'primary', align, wrap, uppercase = false } = defineProps<PiaPhxsgHeadingProps>();

const isPiaHeadingVariant = (value: string | undefined): value is PiaHeadingVariant => {
  return value !== undefined && headingVariants.includes(value as PiaHeadingVariant);
};

const normalizedUse = computed<PiaHeadingVariant>(() => {
  if (isPiaHeadingVariant(use)) {
    return use;
  }

  return 'h2';
});

const variantToUse = computed<PiaHeadingVariant>(() => {
  if (isPiaHeadingVariant(variant)) {
    return variant;
  }

  return normalizedUse.value;
});

const coreVariant = computed(() => {
  const v = variantToUse.value;

  if (v === 'h1' || v === 'h2' || v === 'h3') {
    return 'display';
  }

  if (v === 'h4') {
    return 'headline';
  }

  return 'body';
});

const coreSize = computed(() => {
  const v = variantToUse.value;

  if (v === 'h1') {
    return 'large';
  }

  if (v === 'h2' || v === 'h3' || v === 'h4') {
    return 'medium';
  }

  return 'small';
});

const headingClasses = computed(() => [
  `phxsg-${variantToUse.value}`,
  {
    'phxsg-text--secondary': color === 'secondary',
    'phxsg-text--subtle': color === 'subtle',
    'phxsg-text--muted': color === 'muted',
  },
]);
</script>

<template>
  <PhxsgCoreHeading
    :use="use"
    :variant="coreVariant"
    :size="coreSize"
    :align="align"
    :wrap="wrap"
    :uppercase="uppercase"
    :class="headingClasses"
  >
    <slot />
  </PhxsgCoreHeading>
</template>

<style src="./heading.css" />
